/**
 * AdminPayGlocal — manage the PayGlocal payment gateway from the admin panel.
 *
 * Three sections:
 *   1. Mode + credentials form (UAT ↔ PROD flip, MID, KIDs, callback URLs)
 *   2. PEM file uploaders (private merchant key + PayGlocal public cert)
 *   3. Test-connection card (fires a probe call to PayGlocal so you can
 *      validate the handshake without running a real checkout)
 *   4. Transactions list (all orders processed through PayGlocal)
 */
import React, { useEffect, useState } from "react";
import {
  Loader2, Shield, Settings as SettingsIcon, Upload, CheckCircle2, XCircle,
  AlertTriangle, FlaskConical, Globe2, RefreshCcw, ExternalLink, FileSearch, Lock, Trash2, Activity,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const FIELDS = [
  ["merchant_id", "Merchant ID (MID)", "ptplsurajit..."],
  ["parent_mid", "Parent MID (optional, portfolio accounts only)", ""],
  ["private_kid", "Merchant Private Key KID", "kId-XXXXXXXXXXXXX"],
  ["public_kid", "PayGlocal Public Cert KID", "kId-XXXXXXXXXXXXX"],
  ["callback_url", "Callback URL (PayGlocal → us)", "https://optivize.digital/api/webhooks/payglocal"],
  ["success_url", "Customer success-return URL", "https://optivize.digital/checkout/payglocal/success"],
  ["failure_url", "Customer failure-return URL", "https://optivize.digital/checkout/payglocal/failed"],
];

export default function AdminPayGlocal() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [privateFile, setPrivateFile] = useState(null);
  const [publicFile, setPublicFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);

  const runHealth = async () => {
    setHealthLoading(true);
    try {
      const { data } = await api.get("/admin/payglocal/health");
      setHealth(data);
    } catch (e) {
      setHealth({
        status: "error",
        message: e?.response?.data?.detail || "Health check failed",
        checks: {},
      });
    } finally {
      setHealthLoading(false);
    }
  };

  const load = async () => {
    const { data } = await api.get("/admin/payglocal/config");
    setCfg(data);
    setForm({
      merchant_id: data.merchant_id || "",
      parent_mid: data.parent_mid || "",
      private_kid: data.private_kid || "",
      public_kid: data.public_kid || "",
      callback_url: data.callback_url || "",
      success_url: data.success_url || "",
      failure_url: data.failure_url || "",
      encrypt_payload: data.encrypt_payload !== false,
    });
    const { data: tx } = await api.get("/admin/payglocal/transactions?limit=100");
    setTransactions(tx);
    runHealth();
  };
  useEffect(() => { load(); }, []);

  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const saveConfig = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const { data } = await api.put("/admin/payglocal/config", form);
      setCfg(data);
      toast.success("PayGlocal config saved — applied immediately");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const uploadKeys = async (e) => {
    e?.preventDefault?.();
    if (!privateFile && !publicFile) { toast.error("Pick at least one file"); return; }
    const fd = new FormData();
    if (privateFile) fd.append("private_key", privateFile);
    if (publicFile)  fd.append("public_cert", publicFile);
    if (form?.private_kid) fd.append("private_kid", form.private_kid);
    if (form?.public_kid)  fd.append("public_kid",  form.public_kid);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/payglocal/keys", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCfg(data);
      setPrivateFile(null); setPublicFile(null);
      toast.success(`Saved: ${(data.saved || []).join(" + ") || "config only"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const testConn = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/admin/payglocal/test");
      setTestResult(data);
    } catch (e) {
      setTestResult({ http_status: "ERR", response: { detail: e?.response?.data?.detail || String(e) }, interpretation: "Local error before PayGlocal call" });
    } finally { setTesting(false); }
  };

  const clearField = async (fieldKey, label) => {
    if (!window.confirm(`Remove ${label}?\n\nThis unlocks the field so you can re-enter a new value.`)) return;
    try {
      const { data } = await api.delete(`/admin/payglocal/config?fields=${fieldKey}`);
      setCfg(data);
      setForm(f => ({ ...f, [fieldKey]: "" }));
      toast.success(`${label} cleared — you can now enter a new value`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    }
  };

  const deletePem = async (which, label) => {
    if (!window.confirm(`Remove the saved ${label}?\n\nOnce removed you'll need to upload the PEM again before payments can process.`)) return;
    try {
      const { data } = await api.delete(`/admin/payglocal/keys?which=${which}`);
      setCfg(data);
      toast.success(`${label} removed — upload replacement to continue.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const locked = cfg?.locked || {};

  if (!cfg || !form) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-cyan-300" size={26}/></div>;

  return (
    <div className="space-y-5 p-1" data-testid="admin-payglocal-page">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-white inline-flex items-center gap-2" style={{fontFamily:'Outfit'}}>
            <Shield size={20} className="text-cyan-300"/> PayGlocal Gateway
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Live cross-border / multi-currency card payments via PayGlocal.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <HealthBadge health={health} loading={healthLoading} onRefresh={runHealth}/>
          <span
            className="px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
            data-testid="pg-mode-badge"
          >
            🟢 LIVE · {cfg.base_url}
          </span>
        </div>
      </header>

      {/* Status strip */}
      <div className="grid sm:grid-cols-4 gap-3" data-testid="pg-status-strip">
        <StatPill label="Configured" value={cfg.is_configured ? "Yes" : "No"} good={cfg.is_configured}/>
        <StatPill label="Private key" value={cfg.private_key_present ? "Loaded" : "Missing"} good={cfg.private_key_present}/>
        <StatPill label="Public cert"  value={cfg.public_cert_present  ? "Loaded" : "Missing"} good={cfg.public_cert_present}/>
        <StatPill label="MID"          value={cfg.merchant_id || "—"}    good={!!cfg.merchant_id}/>
      </div>

      {/* Credentials form */}
      <form onSubmit={saveConfig} className="card p-5 space-y-4" data-testid="pg-config-form">
        <div className="flex items-center gap-2">
          <SettingsIcon size={16} className="text-cyan-300"/>
          <h2 className="text-white font-semibold">Credentials</h2>
        </div>
        <p className="text-[11px] text-slate-500 -mt-2">
          PayGlocal runs in production mode only. Base URL: <code className="text-cyan-400">{cfg.prod_base_url || "https://api.payglocal.in"}</code>
        </p>

        <div className="grid md:grid-cols-2 gap-3">
          {FIELDS.map(([k, label, placeholder]) => {
            const isLocked = Boolean(locked[k]);
            return (
              <div key={k} className="md:col-span-1">
                <div className="flex items-center justify-between mb-1">
                  <label className="label !mb-0">{label}</label>
                  {isLocked && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-300 bg-amber-500/10 border border-amber-500/40 px-1.5 py-0.5 rounded-full" data-testid={`pg-locked-${k.replace(/_/g,'-')}`}>
                      <Lock size={9}/> Locked
                    </span>
                  )}
                </div>
                <div className="flex gap-1.5">
                  <input
                    value={form[k] || ""}
                    onChange={setF(k)}
                    placeholder={placeholder}
                    readOnly={isLocked}
                    className={`input w-full text-sm font-mono ${isLocked ? "opacity-70 cursor-not-allowed" : ""}`}
                    data-testid={`pg-input-${k.replace(/_/g,'-')}`}
                  />
                  {isLocked && (
                    <button
                      type="button"
                      onClick={()=>clearField(k, label)}
                      className="btn-ghost !py-2 !px-2.5 shrink-0"
                      title="Remove & unlock"
                      data-testid={`pg-clear-${k.replace(/_/g,'-')}`}
                    >
                      <Trash2 size={13} className="text-rose-400"/>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          <div className="md:col-span-2 flex items-center gap-2">
            <input id="pg-encrypt" type="checkbox" checked={!!form.encrypt_payload} onChange={setF("encrypt_payload")} data-testid="pg-input-encrypt"/>
            <label htmlFor="pg-encrypt" className="text-sm text-slate-300">Encrypt request payload (JWE) — PayGlocal default</label>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={load} className="btn-ghost inline-flex items-center gap-1.5 text-sm" data-testid="pg-reload-btn">
            <RefreshCcw size={13}/> Reload
          </button>
          <button type="submit" disabled={saving} className="btn-primary inline-flex items-center gap-1.5 text-sm" data-testid="pg-save-btn">
            {saving ? <Loader2 size={14} className="animate-spin"/> : <CheckCircle2 size={14}/>} Save config
          </button>
        </div>
      </form>

      {/* PEM uploader */}
      <form onSubmit={uploadKeys} className="card p-5 space-y-4" data-testid="pg-keys-form">
        <div className="flex items-center gap-2">
          <Upload size={16} className="text-cyan-300"/>
          <h2 className="text-white font-semibold">PEM key files</h2>
        </div>
        <p className="text-xs text-slate-400 -mt-2">
          Download from GCC Dashboard → Key Management. Files are stored privately on the server AND persisted in the database so they survive re-deploys. Once uploaded, a key is <strong className="text-amber-300">locked</strong> until you explicitly remove it below.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <PemPicker
            label="Merchant Private Key"
            hint="kId-XXX_ptplsurajitXXXX.pem (BEGIN PRIVATE KEY)"
            file={privateFile}
            onChange={(f)=>setPrivateFile(f)}
            present={cfg.private_key_present}
            locked={Boolean(locked.private_key)}
            uploadedAt={cfg.private_key_uploaded_at}
            onDelete={()=>deletePem("private", "Merchant Private Key")}
            testid="pg-upload-private"
          />
          <PemPicker
            label="PayGlocal Public Cert"
            hint="XXXX_payglocal_mid.pem (BEGIN PUBLIC KEY)"
            file={publicFile}
            onChange={(f)=>setPublicFile(f)}
            present={cfg.public_cert_present}
            locked={Boolean(locked.public_cert)}
            uploadedAt={cfg.public_cert_uploaded_at}
            onDelete={()=>deletePem("public", "PayGlocal Public Cert")}
            testid="pg-upload-public"
          />
        </div>
        <div className="flex justify-end">
          <button type="submit" disabled={uploading || (!privateFile && !publicFile)} className="btn-primary inline-flex items-center gap-1.5 text-sm" data-testid="pg-upload-btn">
            {uploading ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14}/>} Upload keys
          </button>
        </div>
      </form>

      {/* Test connection */}
      <div className="card p-5 space-y-3" data-testid="pg-test-card">
        <div className="flex items-center gap-2">
          <FlaskConical size={16} className="text-cyan-300"/>
          <h2 className="text-white font-semibold">Test connection</h2>
        </div>
        <p className="text-xs text-slate-400">
          Fires a $1.00 USD probe to PayGlocal with a sample John Denver billing block. Doesn&apos;t charge anyone — just validates the handshake.
        </p>
        <button onClick={testConn} disabled={testing} className="btn-primary inline-flex items-center gap-1.5 text-sm" data-testid="pg-test-btn">
          {testing ? <Loader2 size={14} className="animate-spin"/> : <Globe2 size={14}/>} Run test
        </button>

        {testResult && (
          <div className="mt-2 space-y-2" data-testid="pg-test-result">
            <div className="flex items-center gap-2">
              {testResult.http_status >= 200 && testResult.http_status < 300 ? (
                <CheckCircle2 size={16} className="text-emerald-400"/>
              ) : (
                <XCircle size={16} className="text-rose-400"/>
              )}
              <code className="text-xs text-slate-300">HTTP {testResult.http_status}</code>
              <span className="text-xs text-slate-500">·</span>
              <span className="text-xs text-slate-300">MID {testResult.merchant_id || "—"}</span>
            </div>
            <div className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 inline-flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0"/>
              <span>{testResult.interpretation}</span>
            </div>
            <details className="text-xs">
              <summary className="text-slate-400 cursor-pointer">Raw PayGlocal response</summary>
              <pre className="mt-1 p-2 bg-slate-950/60 rounded text-slate-300 overflow-x-auto" data-testid="pg-test-raw">{JSON.stringify(testResult.response, null, 2)}</pre>
            </details>
          </div>
        )}
      </div>

      {/* Transactions */}
      <div className="card p-5 space-y-3" data-testid="pg-transactions-card">
        <div className="flex items-center justify-between">
          <h2 className="text-white font-semibold inline-flex items-center gap-2">
            <FileSearch size={16} className="text-cyan-300"/> PayGlocal transactions ({transactions?.total ?? 0})
          </h2>
          <button onClick={load} className="btn-ghost text-xs inline-flex items-center gap-1.5" data-testid="pg-tx-refresh">
            <RefreshCcw size={12}/> Refresh
          </button>
        </div>
        <div className="grid grid-cols-4 gap-2 text-xs">
          {transactions && Object.entries(transactions.buckets).map(([k,v])=>(
            <div key={k} className="rounded-lg bg-slate-900/60 border border-white/5 px-3 py-2">
              <div className="text-[10px] uppercase text-slate-500 tracking-wider">{k}</div>
              <div className="text-lg font-bold text-white" data-testid={`pg-bucket-${k}`}>{v}</div>
            </div>
          ))}
        </div>

        <div className="overflow-x-auto" data-testid="pg-tx-table">
          <table className="w-full text-xs">
            <thead className="text-left text-slate-500 border-b border-white/5">
              <tr>
                <th className="py-2 pr-2">Order</th>
                <th className="py-2 pr-2">PayGlocal GID</th>
                <th className="py-2 pr-2">Amount</th>
                <th className="py-2 pr-2">Status</th>
                <th className="py-2 pr-2">Initiated</th>
                <th className="py-2 pr-2 text-right">Customer</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {(transactions?.rows || []).map(o => (
                <tr key={o.id} data-testid={`pg-tx-row-${o.id}`}>
                  <td className="py-2 pr-2 font-mono text-cyan-300">#{o.id.slice(0,8).toUpperCase()}</td>
                  <td className="py-2 pr-2 font-mono text-slate-300">{o.payglocal_gid || "—"}</td>
                  <td className="py-2 pr-2 text-white">{o.payglocal_amount || "—"} {o.payglocal_currency || ""}</td>
                  <td className="py-2 pr-2">
                    <span className={
                      o.status === "paid" ? "text-emerald-300" :
                      ["refunded","partially_refunded"].includes(o.status) ? "text-pink-300" :
                      ["rejected","cancelled","fraud"].includes(o.status) ? "text-rose-300" :
                      "text-amber-300"
                    }>{o.status?.replace(/_/g,' ') || "—"}</span>
                    {o.payglocal_status && <div className="text-[10px] text-slate-500 mt-0.5">PG: {o.payglocal_status}</div>}
                  </td>
                  <td className="py-2 pr-2 text-slate-400">{(o.payglocal_initiated_at||"").slice(0,16).replace("T"," ")}</td>
                  <td className="py-2 pr-2 text-right text-slate-300">{o.customer_email || o.user_id?.slice(0,8)}</td>
                </tr>
              ))}
              {!transactions?.rows?.length && (
                <tr><td colSpan={6} className="py-6 text-center text-slate-500 italic" data-testid="pg-tx-empty">No PayGlocal transactions yet. Once the gateway is activated, transactions will appear here automatically.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="text-[11px] text-slate-500 mt-2 flex items-center gap-1.5">
          <ExternalLink size={11}/> Webhook endpoint: <code className="text-cyan-400">{cfg.callback_url || `${cfg.base_url}/gl/v1/payments/merchantCallback`}</code>
        </div>
      </div>
    </div>
  );
}

function StatPill({ label, value, good }) {
  return (
    <div className={`rounded-lg p-3 border ${good ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5"}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-sm font-semibold mt-0.5 ${good ? "text-emerald-200" : "text-amber-200"}`}>{value}</div>
    </div>
  );
}

function HealthBadge({ health, loading, onRefresh }) {
  const status = health?.status || (loading ? "loading" : "unknown");
  // Style + label per status
  const map = {
    ok: {
      cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
      icon: <CheckCircle2 size={12} className="text-emerald-300"/>,
      label: "Verified live",
    },
    warn: {
      cls: "bg-amber-500/15 text-amber-200 border-amber-500/40",
      icon: <AlertTriangle size={12} className="text-amber-300"/>,
      label: "Warning",
    },
    error: {
      cls: "bg-rose-500/15 text-rose-300 border-rose-500/40",
      icon: <XCircle size={12} className="text-rose-300"/>,
      label: "Not verified",
    },
    loading: {
      cls: "bg-slate-500/15 text-slate-300 border-slate-500/40",
      icon: <Loader2 size={12} className="animate-spin"/>,
      label: "Checking…",
    },
    unknown: {
      cls: "bg-slate-500/15 text-slate-300 border-slate-500/40",
      icon: <Activity size={12}/>,
      label: "—",
    },
  };
  const m = map[status] || map.unknown;
  const c = health?.checks || {};
  const tooltip = health
    ? [
        health.message,
        "",
        `Crypto: ${c.crypto_ok ? "OK" : "FAIL"}${c.jwe_bytes ? ` (JWE ${c.jwe_bytes}B · JWS ${c.jws_bytes}B)` : ""}`,
        `Base URL: ${c.base_url_reachable ? `reachable (HTTP ${c.base_url_http_status})` : "unreachable"}`,
        `MID: ${c.merchant_id || "—"}`,
        `Private KID: ${c.private_kid || "—"}`,
        `Public KID: ${c.public_kid || "—"}`,
      ].join("\n")
    : "Running dry-run JWE + JWS + reachability check…";

  return (
    <button
      type="button"
      onClick={onRefresh}
      title={tooltip}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 border transition hover:scale-[1.02] active:scale-95 ${m.cls}`}
      data-testid="pg-health-badge"
    >
      {m.icon}
      <span data-testid="pg-health-status">{m.label}</span>
      <RefreshCcw size={10} className="opacity-60"/>
    </button>
  );
}


function ReadRow({ label, value, mono, good }) {
  return (
    <div className="flex items-baseline gap-2 min-w-0">
      <span className="text-slate-500 shrink-0">{label}:</span>
      <span className={`truncate ${mono ? "font-mono" : ""} ${good === false ? "text-rose-300" : good === true ? "text-emerald-300" : "text-slate-200"}`}>{value || "—"}</span>
    </div>
  );
}

function PemPicker({ label, hint, file, onChange, present, testid, locked = false, uploadedAt, onDelete }) {
  // When a PEM is locked (saved on disk), we render a compact "installed"
  // card with a Remove & unlock button instead of the drop-zone. This
  // prevents accidental overwrites and matches the operator's spec.
  if (locked) {
    return (
      <div data-testid={`${testid}-locked`}>
        <div className="flex items-center justify-between mb-1.5">
          <label className="label !mb-0">{label}</label>
          <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold text-amber-300 bg-amber-500/10 border border-amber-500/40 px-1.5 py-0.5 rounded-full">
            <Lock size={9}/> Locked
          </span>
        </div>
        <div className="flex items-center justify-between gap-3 border border-emerald-500/40 bg-emerald-500/5 rounded-xl px-4 py-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-9 w-9 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0">
              <CheckCircle2 size={18} className="text-emerald-400"/>
            </div>
            <div className="min-w-0">
              <div className="text-emerald-200 text-sm font-semibold">Installed &amp; persisted</div>
              <div className="text-slate-400 text-[11px] truncate">
                {uploadedAt ? `Uploaded ${new Date(uploadedAt).toLocaleString()}` : "Loaded from database"} · survives reboots
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onDelete}
            className="btn-ghost !py-1.5 !px-2.5 inline-flex items-center gap-1 shrink-0"
            title="Remove & unlock"
            data-testid={`${testid}-delete`}
          >
            <Trash2 size={13} className="text-rose-400"/>
            <span className="text-xs">Remove</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="label !mb-0">{label}</label>
        <span className={`text-[10px] uppercase font-semibold ${present ? "text-emerald-400" : "text-amber-300"}`}>
          {present ? "✓ on disk" : "missing"}
        </span>
      </div>
      <label
        htmlFor={testid}
        className={`flex flex-col items-center justify-center w-full border-2 border-dashed rounded-xl px-4 py-5 cursor-pointer transition ${
          file ? "border-emerald-500/60 bg-emerald-500/5" : "border-slate-700 hover:border-cyan-500 hover:bg-cyan-500/5"
        }`}
        data-testid={`${testid}-drop`}
      >
        {file ? (
          <>
            <CheckCircle2 size={20} className="text-emerald-400 mb-1.5"/>
            <span className="text-emerald-200 text-sm font-semibold truncate max-w-full">{file.name}</span>
            <span className="text-slate-400 text-[11px] mt-0.5">{(file.size/1024).toFixed(1)} KB · click to replace</span>
          </>
        ) : (
          <>
            <Upload size={20} className="text-slate-400 mb-1.5"/>
            <span className="text-slate-200 text-sm">Click to select PEM</span>
            <span className="text-slate-500 text-[11px] mt-0.5">{hint}</span>
          </>
        )}
      </label>
      <input id={testid} type="file" accept=".pem,application/x-pem-file,application/x-x509-ca-cert,text/plain" onChange={e=>onChange(e.target.files?.[0] || null)} className="hidden" data-testid={testid}/>
    </div>
  );
}
